import { useNavigate } from 'react-router-dom';
import { collection, getDocs,getDoc, query, orderBy, where, limit, startAfter,doc,runTransaction,updateDoc,addDoc } from 'firebase/firestore';
import { db,auth } from '../../config/firebase';
import { useEffect, useState } from 'react';
import { FiArrowLeft, FiChevronDown, FiSearch, FiFilter } from 'react-icons/fi';
import '../../styles/Tracking.css';

const SuperAdminTrackingReportsPage = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTenants, setExpandedTenants] = useState({});
  const [tenantNames, setTenantNames] = useState({});
  const [locationNames, setLocationNames] = useState({});
  const [roomNames, setRoomNames] = useState({});
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [pendingReverts, setPendingReverts] = useState([]);
  const [revertReports, setRevertReports] = useState([]);
  // const [showNotificationMessage] = useState(false);
  const [filters, setFilters] = useState({
    tenantName: '',
    locationName: '',
    roomName: '',
    deviceName: '',
    dateFrom: '',
    dateTo: '',
    actionType: '' // 'note_update', 'device_change', 'standby_replacement', etc.
  });

  // Group reports by tenant
  const groupedReports = reports.reduce((groups, report) => {
    const tenantId = report.tenantId || 'Unknown';
    if (!groups[tenantId]) {
      groups[tenantId] = [];
    }
    groups[tenantId].push(report);
    return groups;
  }, {});


  useEffect(() => {
  const fetchRevertRequests = async () => {
    const q = query(collection(db, "revert_requests"), where("status", "==", "pending"));
    const snapshot = await getDocs(q);
    const requests = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setPendingReverts(requests);
  };
  fetchRevertRequests();
  fetchTrackingReports(true); 
}, []);


  // Fetch all reference data (tenants, locations, rooms)
  const fetchReferenceData = async () => {
    try {
      const tenantNamesMap = {};
      const locationNamesMap = {};
      const roomNamesMap = {};

      // Fetch all tenants
      const tenantsSnapshot = await getDocs(collection(db, 'tenants'));
      
      for (const tenantDoc of tenantsSnapshot.docs) {
        const tenantData = tenantDoc.data();
        const tenantId = tenantDoc.id;
        tenantNamesMap[tenantId] = tenantData.name || tenantData.tenantName || tenantId;

        // Fetch locations for this tenant
        try {
          const locationsSnapshot = await getDocs(
            collection(db, 'tenants', tenantId, 'locations')
          );
          
          for (const locationDoc of locationsSnapshot.docs) {
            const locationData = locationDoc.data();
            const locationId = locationDoc.id;
            const locationKey = `${tenantId}_${locationId}`;
            locationNamesMap[locationKey] = locationData.name || locationData.locationName || locationId;

            // Fetch rooms for this location
            try {
              const roomsSnapshot = await getDocs(
                collection(db, 'tenants', tenantId, 'locations', locationId, 'rooms')
              );
              
              for (const roomDoc of roomsSnapshot.docs) {
                const roomData = roomDoc.data();
                const roomId = roomDoc.id;
                const roomKey = `${tenantId}_${locationId}_${roomId}`;
                roomNamesMap[roomKey] = roomData.name || roomData.roomName || roomId;
              }
            } catch (err) {
              console.warn(`Error fetching rooms for ${tenantId}/${locationId}:`, err);
            }
          }
        } catch (err) {
          console.warn(`Error fetching locations for tenant ${tenantId}:`, err);
        }
      }

      setTenantNames(tenantNamesMap);
      setLocationNames(locationNamesMap);
      setRoomNames(roomNamesMap);
    } catch (err) {
      console.error('Error fetching reference data:', err);
    }
  };

const fetchTrackingReports = async (reset = false) => {
  try {
    setLoading(true);
    if (reset) {
      setReports([]);
      setRevertReports([]);
      setLastDoc(null);
      setHasMore(true);
      await fetchReferenceData();
    }

    const queryConstraints = [orderBy("timestamp", "desc")];

    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      queryConstraints.push(where("timestamp", ">=", fromDate.toISOString()));
    }

    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo + "T23:59:59");
      queryConstraints.push(where("timestamp", "<=", toDate.toISOString()));
    }

    if (filters.actionType) {
      queryConstraints.push(where("actionType", "==", filters.actionType));
    }

    queryConstraints.push(limit(50));
    if (lastDoc && !reset) {
      queryConstraints.push(startAfter(lastDoc));
    }

    const q = query(collection(db, "tracking_reports"), ...queryConstraints);
    const snapshot = await getDocs(q);

    const allReports = [];
    const revertLogs = [];

    snapshot.forEach((doc) => {
      const data = doc.data();
      const formatted = {
        id: doc.id,
        ...data,
        formattedDate: formatDate(data.timestamp),
        formattedTime: formatTime(data.timestamp),
      };

      // ✅ If reverted is true, push to Revert History
      if (data.reverted === true) {
        revertLogs.push(formatted);
      } else {
        allReports.push(formatted);
      }
    });

    // ✅ Apply text filters function
    const applyTextFilters = (list) => {
      return list.filter((r) => {
        if (filters.tenantName) {
          const tName = tenantNames[r.tenantId] || r.tenantId || "";
          if (!tName.toLowerCase().includes(filters.tenantName.toLowerCase())) return false;
        }
        if (filters.locationName) {
          const key = `${r.tenantId}_${r.locationId}`;
          const lName = locationNames[key] || r.locationName || r.locationId || "";
          if (!lName.toLowerCase().includes(filters.locationName.toLowerCase())) return false;
        }
        if (filters.roomName) {
          const key = `${r.tenantId}_${r.locationId}_${r.roomId}`;
          const rName = roomNames[key] || r.roomName || r.roomId || "";
          if (!rName.toLowerCase().includes(filters.roomName.toLowerCase())) return false;
        }
        if (filters.deviceName) {
          const dName = r.deviceSnapshot?.name || r.deviceName || "";
          if (!dName.toLowerCase().includes(filters.deviceName.toLowerCase())) return false;
        }
        return true;
      });
    };

    // ✅ Apply filters separately to each list
    const filteredReverts = applyTextFilters(revertLogs);
    const filteredStandby = applyTextFilters(allReports);

    if (reset) {
      setReports(filteredStandby);
      setRevertReports(filteredReverts);
    } else {
      setReports((prev) => [...prev, ...filteredStandby]);
      setRevertReports((prev) => [...prev, ...filteredReverts]);
    }

    const lastVisible = snapshot.docs[snapshot.docs.length - 1];
    setLastDoc(lastVisible);
    setHasMore(snapshot.docs.length === 50);
  } catch (err) {
    console.error("Error fetching tracking reports:", err);
    setError(err.message);
  } finally {
    setLoading(false);
  }
};


  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid Date';
    }
  };

// Fallback if showNotificationMessage is not globally defined
const showNotificationMessage = (msg, type = "info") => {
  alert(`[${type.toUpperCase()}] ${msg}`);
};

const handleApproveRevert = async (requestId) => {
  try {
    const timestamp = new Date().toISOString();
    const user = auth.currentUser;
    const requestRef = doc(db, "revert_requests", requestId);

    // ✅ Step 1: Approve the revert request
    await updateDoc(requestRef, {
      isApproved: true,
      isCompleted: true,
      status: "approved",
      approvedAt: timestamp,
      approvedBy: user?.uid || "system"
    });

    // ✅ Step 2: Get request data
    const requestSnap = await getDoc(requestRef);
    if (!requestSnap.exists()) throw new Error("Revert request not found");
    const requestData = requestSnap.data();

    const {
      tenantId,
      locationId,
      roomId,
      currentDevice,
      previousDevice,
      requestedBy
    } = requestData;

    if (!tenantId || !locationId || !roomId || !currentDevice?.id || !previousDevice?.id) {
      throw new Error("Missing device or location info");
    }

    // ✅ Step 3: Revert device
    await revertDeviceToPrevious(
      currentDevice.id,
      previousDevice,
      tenantId,
      locationId,
      roomId
    );

    // ✅ Step 4: Look for the original standby tracking entry
    const trackingQuery = query(
      collection(db, "tracking_reports"),
      where("actionType", "==", "standby"),
      where("deviceId", "==", currentDevice.id),
      limit(1)
    );

    const snapshot = await getDocs(trackingQuery);
    if (!snapshot.empty) {
      const ref = snapshot.docs[0].ref;

      await updateDoc(ref, {
        reverted: true,
        revertTimestamp: timestamp,
        revertMetadata: {
          revertedTo: previousDevice,
          approvedBy: user?.uid || "system",
          approvedByEmail: user?.email || "",
          approvedForUser: requestedBy?.email || "Unknown"
        }
      });
    } else {
      // If not found, fallback: create standby entry
      await addDoc(collection(db, "tracking_reports"), {
        tenantId,
        locationId,
        roomId,
        deviceId: currentDevice.id,
        timestamp,
        actionType: "standby",
        revertedFrom: currentDevice,
        revertedTo: previousDevice,
        metadata: {
          performedBy: user?.uid || "system",
          performedByEmail: user?.email || "",
          approvedForUser: requestedBy?.email || "Unknown"
        }
      });
    }

    showNotificationMessage("✅ Revert approved and history updated", "success");
  } catch (err) {
    console.error("❌ Approval failed:", err);
    showNotificationMessage(`Approval failed: ${err.message}`, "error");
  }
};


// Helper to revert device
const revertDeviceToPrevious = async (currentDeviceId, previousDevice, tenantId, locationId, roomId) => {
  const roomRef = doc(db, "tenants", String(tenantId), "locations", String(locationId), "rooms", String(roomId));
  const roomDoc = await getDoc(roomRef);
  if (!roomDoc.exists()) throw new Error("Room not found");

  const roomData = roomDoc.data();
  const devices = Array.isArray(roomData.devices) ? roomData.devices : [];

  const updatedDevices = devices.map(device =>
    device.id === currentDeviceId
      ? { ...previousDevice, lastUpdated: new Date().toISOString() }
      : device
  );

  await updateDoc(roomRef, {
    devices: updatedDevices,
    lastModified: new Date().toISOString()
  });
};




  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return 'Invalid Time';
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    fetchTrackingReports(true);
  };

  const handleResetFilters = () => {
    setFilters({
      tenantName: '',
      locationName: '',
      roomName: '',
      deviceName: '',
      dateFrom: '',
      dateTo: '',
      actionType: ''
    });
    setTimeout(() => fetchTrackingReports(true), 100);
  };

  const handleBackToDashboard = () => {
    navigate('/admin');
  };

  const toggleTenantDropdown = (tenantId) => {
    setExpandedTenants(prev => ({
      ...prev,
      [tenantId]: !prev[tenantId]
    }));
  };


  const loadMoreReports = () => {
    if (!loading && hasMore) {
      fetchTrackingReports(false);
    }
  };

  const getActionTypeDisplay = (actionType) => {
    const actionTypes = {
      'note_update': 'Note Updated',
      'device_added': 'Device Added',
      'device_removed': 'Device Removed',
      'device_modified': 'Device Modified',
      'standby_replacement': 'Standby Device Replaced',
      'device_relocated': 'Device Relocated',
      'device_status_changed': 'Status Changed'
    };
    return actionTypes[actionType] || actionType || 'Update';
  };

  const renderReportsTable = (tenantReports) => (
    <div className="table-container">
      <table className="reports-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Time</th>
            <th>Action</th>
            <th>Location</th>
            <th>Room</th>
            <th>Device</th>
            <th>Make/Model</th>
            <th>Serial</th>
            <th>Note/Change</th>
            <th>Previous Device</th>
          </tr>
        </thead>
        <tbody>
          {tenantReports.map((report) => {
            const locationKey = `${report.tenantId}_${report.locationId}`;
            const roomKey = `${report.tenantId}_${report.locationId}_${report.roomId}`;
            
            return (
              <tr key={report.id}>
                <td>{report.formattedDate}</td>
                <td>{report.formattedTime}</td>
                <td>
                  <span className={`action-badge ${report.actionType || 'default'}`}>
                    {getActionTypeDisplay(report.actionType)}
                  </span>
                </td>
                <td>{locationNames[locationKey] || report.locationName || 'Unknown Location'}</td>
                <td>{roomNames[roomKey] || report.roomName || 'Unknown Room'}</td>
                <td>{report.deviceSnapshot?.name || report.deviceName || 'Unknown Device'}</td>
                <td>
                  {report.deviceSnapshot?.make} {report.deviceSnapshot?.model}
                </td>
                <td>{report.deviceSnapshot?.serialNumber || '-'}</td>
                <td className="note-cell" title={report.note || report.changeDescription}>
                  {report.note || report.changeDescription || '-'}
                </td>
                <td>
                  {report.previousDevice ? (
                    <div className="previous-device">
                      <div>{report.previousDevice.name}</div>
                      <small>{report.previousDevice.serialNumber}</small>
                    </div>
                  ) : '-'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  if (loading && reports.length === 0 && revertReports.length === 0) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading tracking reports...</p>
      </div>
    );
  }

  // ✅ Check if we have any data at all (standby OR revert history)
  const hasAnyData = Object.keys(groupedReports).length > 0 || revertReports.length > 0;

  return (
  <div className="admin-container">
    <div className="admin-dashboard">
      <div className="admin-header">
        <button className="back-button" onClick={handleBackToDashboard}>
          <FiArrowLeft size={20} /> Back to Dashboard
        </button>
        <h1 className="admin-title">Device Tracking Reports</h1>
      </div>

        {/* Enhanced Filters Section */}
        <div className="filters-section">
          <div className="filter-grid">
            <div className="filter-item">
              <label><FiSearch size={14} /> Tenant Name</label>
              <input
                type="text"
                name="tenantName"
                value={filters.tenantName}
                onChange={handleFilterChange}
                placeholder="Search by tenant name"
              />
            </div>
            <div className="filter-item">
              <label><FiSearch size={14} /> Location Name</label>
              <input
                type="text"
                name="locationName"
                value={filters.locationName}
                onChange={handleFilterChange}
                placeholder="Search by location name"
              />
            </div>
            <div className="filter-item">
              <label><FiSearch size={14} /> Room Name</label>
              <input
                type="text"
                name="roomName"
                value={filters.roomName}
                onChange={handleFilterChange}
                placeholder="Search by room name"
              />
            </div>
            <div className="filter-item">
              <label><FiSearch size={14} /> Device Name</label>
              <input
                type="text"
                name="deviceName"
                value={filters.deviceName}
                onChange={handleFilterChange}
                placeholder="Search by device name"
              />
            </div>
            <div className="filter-item">
              <label><FiFilter size={14} /> Action Type</label>
              <select
                name="actionType"
                value={filters.actionType}
                onChange={handleFilterChange}
              >
                <option value="">All Actions</option>
                <option value="note_update">Note Updates</option>
                <option value="device_modified">Device Modified</option>
                <option value="standby">Standby Replacements</option>
              </select>
            </div>
            <div className="filter-item">
              <label>From Date</label>
              <input
                type="date"
                name="dateFrom"
                value={filters.dateFrom}
                onChange={handleFilterChange}
              />
            </div>
            <div className="filter-item">
              <label>To Date</label>
              <input
                type="date"
                name="dateTo"
                value={filters.dateTo}
                onChange={handleFilterChange}
              />
            </div>
          </div>
          <div className="filter-actions">
            <button
              onClick={handleResetFilters}
              className="secondary"
            >
              Reset Filters
            </button>
            <button
              onClick={handleApplyFilters}
              className="primary"
            >
              Apply Filters
            </button>
          </div>
        </div>

        {/* Enhanced Summary Section */}
{hasAnyData && (
  <div className="summary-section">
    {/* 1. Total Activity */}
    <div className="summary-card">
      <h3>Total Activities</h3>
      <p className="value">{reports.length}</p>
    </div>

    {/* 2. Pending Revert Requests */}
    <div className="summary-card">
      <h3>Pending Reverts</h3>
      <p className="value">
        {pendingReverts.length}
      </p>
    </div>

    {/* 3. Total Completed Reverts */}
    <div className="summary-card">
      <h3>Total Reverted</h3>
      <p className="value">
        {revertReports.length}
      </p>
    </div>

    {/* 4. Active Tenant Count */}
    <div className="summary-card">
      <h3>Active Tenants</h3>
      <p className="value">{Object.keys(groupedReports).length}</p>
    </div>
  </div>
)}

      {/* 🔴 E. Show Pending Revert Requests */}
{pendingReverts?.length > 0 && (
  <div className="pending-reverts-section">
    <h2>Pending Revert Approvals</h2>
    {pendingReverts.map((request) => {
      const { id, previousDevice, currentDevice, requestedBy } = request;

      return (
        <div key={id} className="revert-request-card">
          <div className="device-info-grid">
            <div className="device-block">
              <h4>🔄 Standby Device</h4>
              <p><strong>Name:</strong> {currentDevice?.name || "Unknown"}</p>
              <p><strong>Serial Number:</strong> {currentDevice?.serialNumber || "-"}</p>
              <p><strong>Model:</strong> {currentDevice?.model || "-"}</p>
              <p><strong>Make:</strong> {currentDevice?.make || "-"}</p>
            </div>

            <div className="device-block">
              <h4>↩️ Previous Device</h4>
              <p><strong>Name:</strong> {previousDevice?.name || "Unknown"}</p>
              <p><strong>Serial Number:</strong> {previousDevice?.serialNumber || "-"}</p>
              <p><strong>Model:</strong> {previousDevice?.model || "-"}</p>
              <p><strong>Make:</strong> {previousDevice?.make || "-"}</p>
            </div>
          </div>

          <p><strong>Requested By:</strong> {requestedBy?.email || "Unknown"}</p>

          <button
            onClick={() => handleApproveRevert(request.id)}
            className="approve-button"
          >
            ✅ Approve Revert
          </button>
        </div>
      );
    })}
  </div>
)}


      {/* Error or No Results */}
      {error ? (
        <div className="error-message">
          <p>Error: {error}</p>
          <button onClick={() => fetchTrackingReports(true)} className="retry-button">Retry</button>
        </div>
      ) : !hasAnyData ? (
        <div className="no-results">
          <FiSearch size={48} />
          <h3>No tracking reports found</h3>
          <p>Try adjusting your filters or check back later for new activity.</p>
        </div>
      ) : (
<div className="tenant-dropdown-container">
  {/* 🗂️ StandBy Activity */}
  {Object.keys(groupedReports).length > 0 && (
    <>
      <h2>🗂️ StandBy Activity</h2>
      {Object.entries(groupedReports)
        .sort(([a], [b]) => (tenantNames[a] || a).localeCompare(tenantNames[b] || b))
        .map(([tenantId, tenantReports]) => {
          // ❌ Remove any report that is reverted (reverted === true)
          const filteredReports = tenantReports.filter(r => !r.reverted);

          const groupedByDevice = filteredReports.reduce((acc, report) => {
            const deviceId = report.deviceId || "unknown";
            if (!acc[deviceId]) acc[deviceId] = [];
            acc[deviceId].push(report);
            return acc;
          }, {});

          if (Object.keys(groupedByDevice).length === 0) return null;

          return (
            <div key={tenantId} className="tenant-dropdown">
              <div
                className={`tenant-dropdown-header ${expandedTenants[tenantId] ? "active" : ""}`}
                onClick={() => toggleTenantDropdown(tenantId)}
              >
                <div className="tenant-info">
                  <div className="tenant-name">
                    <span className="primary-name">{tenantNames[tenantId] || "Unknown Tenant"}</span>
                    <span className="tenant-meta">{filteredReports.length} activities</span>
                  </div>
                  <div className="latest-activity">
                    Last: {filteredReports[0]?.formattedDate} {filteredReports[0]?.formattedTime}
                  </div>
                </div>
                <FiChevronDown
                  className={`dropdown-arrow ${expandedTenants[tenantId] ? "rotated" : ""}`}
                  size={20}
                />
              </div>

              <div className={`tenant-reports ${expandedTenants[tenantId] ? "expanded" : ""}`}>
                {Object.entries(groupedByDevice).map(([deviceId, deviceReports]) => {
                  const sample = deviceReports[0];
                  const deviceName = sample?.deviceSnapshot?.name || "Unnamed Device";
                  const location = sample?.locationName || "Unknown Location";
                  const room = sample?.roomName || "Unknown Room";

                  return (
                    <details key={deviceId} className="device-group" open>
                      <summary>
                        <strong>{deviceName}</strong> – <em>{location} / {room}</em>
                        <small style={{ marginLeft: 8 }}>
                          {deviceReports.length} change{deviceReports.length > 1 ? "s" : ""}
                        </small>
                      </summary>
                      {renderReportsTable(deviceReports)}
                    </details>
                  );
                })}
              </div>
            </div>
          );
        })}
    </>
  )}

  {/* Show message if no standby activity but we have revert history */}
  {Object.keys(groupedReports).length === 0 && revertReports.length > 0 && (
    <div className="no-standby-activity">
      <h2>🗂️ StandBy Activity</h2>
      <p>No standby activity found matching your current filters.</p>
    </div>
  )}

  {/* ♻️ Revert History */}
  {revertReports.length > 0 && (
    <>
      <h2 style={{ marginTop: "40px" }}>♻️ Revert History</h2>
      <div className="revert-history-card">
        <table className="revert-table">
          <thead>
            <tr>
              <th>Tenant</th>
              <th>Location / Room</th>
              <th>Rev.To S.no</th>
              <th>Device Name (To)</th>
              <th>Rev.From S.no</th>
              <th>Device Name (From)</th>
              <th>Timestamp</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {revertReports.map((report, index) => {
              const tenantLabel = tenantNames[report.tenantId] || "Unknown Tenant";
              const locationKey = `${report.tenantId}_${report.locationId}`;
              const roomKey = `${report.tenantId}_${report.locationId}_${report.roomId}`;
              const locationLabel = locationNames[locationKey] || report.locationName || "?";
              const roomLabel = roomNames[roomKey] || report.roomName || "?";

              return (
                <tr key={`revert-${report.id}-${index}`}>
                  <td>{tenantLabel}</td>
                  <td>{locationLabel} / {roomLabel}</td>
                  <td>{report.previousDevice?.serialNumber || "-"}</td>
                  <td>{report.previousDevice?.name || "-"}</td>
                  <td>{report.deviceSnapshot?.serialNumber || "-"}</td>
                  <td>{report.deviceSnapshot?.name || "-"}</td>
                  <td>{report.formattedDate} {report.formattedTime}</td>
                  <td><span className="tag-reverted">Reverted</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  )}

  {/* Show message if no revert history but we have standby activity */}
  {revertReports.length === 0 && Object.keys(groupedReports).length > 0 && (
    <div className="no-revert-history">
      <h2 style={{ marginTop: "40px" }}>♻️ Revert History</h2>
      <p>No revert history found matching your current filters.</p>
    </div>
  )}

  {/* Load More */}
  {hasMore && (
    <div className="load-more-container">
      <button
        onClick={loadMoreReports}
        className="load-more-button"
        disabled={loading}
      >
        {loading ? "Loading..." : "Load More Reports"}
      </button>
    </div>
  )}
</div>
      )}
    </div>
  </div>
);
};

export default SuperAdminTrackingReportsPage;